// Shared form components for submission pages
import { useState } from 'react';
import { ImagePlus, X, Loader2 } from 'lucide-react';
import { fileToDataUrl } from '../lib/constants';
import { toast } from 'sonner';

export const FIELD_INPUT = 'w-full bg-white border border-[#F4F1DE] rounded-lg px-4 py-3 outline-none focus:border-[#E07A5F] text-[#2B2D42] placeholder-[#B5B5B5]';
export const FIELD_LABEL = 'block text-sm font-bold text-[#2B2D42] mb-2';

export function ImageUploader({ images, setImages, max = 5, testIdPrefix = 'submission' }) {
  async function handleFiles(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = max - images.length;
    if (remaining <= 0) return toast.error(`Maximum ${max} images`);

    const newImages = [];
    for (const f of files.slice(0, remaining)) {
      try {
        newImages.push(await fileToDataUrl(f));
      } catch (err) {
        toast.error(err.message);
      }
    }
    setImages([...images, ...newImages]);
    e.target.value = '';
  }

  function remove(idx) {
    setImages(images.filter((_, i) => i !== idx));
  }

  return (
    <div>
      <p className={FIELD_LABEL}>
        Photos <span className="text-xs font-normal text-[#B5B5B5]">(up to {max}, max 5MB each)</span>
      </p>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {images.map((src, i) => (
          <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-[#FFE5DD]" data-testid={`${testIdPrefix}-image-${i}`}>
            <img src={src} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => remove(i)}
              data-testid={`${testIdPrefix}-remove-image-${i}`}
              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {images.length < max && (
          <label
            data-testid={`${testIdPrefix}-add-image`}
            className="aspect-square rounded-lg border-2 border-dashed border-[#E07A5F] bg-[#FFE5DD]/30 flex flex-col items-center justify-center cursor-pointer hover:bg-[#FFE5DD]/60"
          >
            <ImagePlus className="w-6 h-6 text-[#C8553D] mb-1" />
            <span className="text-xs font-bold text-[#C8553D]">Add</span>
            <input type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} />
          </label>
        )}
      </div>
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    pending: { label: 'Pending Review', cls: 'bg-[#FFF3D6] text-[#B5841A]' },
    approved: { label: 'Approved', cls: 'bg-[#D5F0EC] text-[#1F6E66]' },
    rejected: { label: 'Rejected', cls: 'bg-[#FFE0DA] text-[#C8553D]' },
  };
  const m = map[status] || { label: status, cls: 'bg-[#F4F1DE] text-[#6D6875]' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${m.cls}`}>
      {m.label}
    </span>
  );
}

export function PremiumGate({ message }) {
  return (
    <div className="bg-gradient-to-br from-[#E9C46A]/30 to-[#E07A5F]/20 border-2 border-[#E9C46A] rounded-2xl p-6 text-center" data-testid="premium-gate">
      <div className="text-3xl mb-2">⭐</div>
      <h3 className="text-lg font-serif font-bold text-[#2B2D42] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
        Premium feature
      </h3>
      <p className="text-sm text-[#6D6875] mb-4">{message || 'Submitting listings is a Premium-only feature.'}</p>
      <a
        href="/app/profile"
        data-testid="premium-gate-cta"
        className="inline-flex items-center gap-2 bg-[#C8553D] hover:bg-[#9E3A26] text-white font-bold px-5 py-2.5 rounded-full text-sm"
      >
        Learn about Premium
      </a>
    </div>
  );
}

export function SubmitButton({ loading, children, testId = 'submit-button' }) {
  return (
    <button
      type="submit"
      disabled={loading}
      data-testid={testId}
      className="w-full bg-[#C8553D] hover:bg-[#9E3A26] disabled:opacity-60 text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 shadow-lg shadow-[#C8553D]/30 transition-colors"
    >
      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : children}
    </button>
  );
}
